import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { 
  BellIcon,
  ChatBubbleLeftIcon,
  HeartIcon,
  UserPlusIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationType } from '@/types/notification';

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'COMMENT':
      return <ChatBubbleLeftIcon className="w-5 h-5 text-blue-500" />;
    case 'LIKE':
      return <HeartIcon className="w-5 h-5 text-red-500" />;
    case 'FOLLOW':
      return <UserPlusIcon className="w-5 h-5 text-purple-500" />;
    case 'WORKFLOW_FORKED':
      return <DocumentDuplicateIcon className="w-5 h-5 text-green-500" />;
    case 'SYSTEM':
      return <InformationCircleIcon className="w-5 h-5 text-gray-500" />;
    default:
      return <BellIcon className="w-5 h-5 text-gray-500" />;
  }
};

export default function NotificationDropdown() {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  
  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    loadMore,
    hasMore,
  } = useNotifications();

  // Handle notification click
  const handleNotificationClick = async (notification: any) => {
    // Mark as read
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type and data
    const { type, data } = notification;
    
    switch (type) {
      case 'COMMENT':
        if (data?.contentType === 'prompt') {
          navigate(`/prompts/${data.contentId}`);
        } else if (data?.contentType === 'workflow') {
          navigate(`/workflows/${data.contentId}`);
        }
        break;
      
      case 'LIKE':
        if (data?.contentType === 'prompt') {
          navigate(`/prompts/${data.contentId}`);
        } else if (data?.contentType === 'workflow') {
          navigate(`/workflows/${data.contentId}`);
        }
        break;
      
      case 'FOLLOW':
        if (data?.followerId) {
          navigate(`/users/${data.followerId}`);
        }
        break;
      
      case 'WORKFLOW_FORKED':
        if (data?.workflowId) {
          navigate(`/workflows/${data.workflowId}`);
        }
        break;
    }

    setIsOpen(false);
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      toast.success('All notifications marked as read.');
    } catch (error) {
      toast.error('Failed to process notifications.');
    }
  };

  // Handle delete notification
  const handleDeleteNotification = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
      toast.success('Notification deleted.');
    } catch (error) {
      toast.error('Failed to delete notification.');
    }
  };

  // Toggle comment preview
  const toggleCommentPreview = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Format comment preview (max 3 lines)
  const formatCommentPreview = (comment: string | undefined) => {
    if (!comment) return null;
    
    // Limit to approximately 150 characters for ~3 lines
    const maxLength = 150;
    const trimmed = comment.length > maxLength 
      ? comment.substring(0, maxLength) + '...' 
      : comment;
    
    // Split by newlines and limit to 3 lines
    const lines = trimmed.split('\n').slice(0, 3);
    return lines.join('\n');
  };

  return (
    <Menu as="div" className="relative" ref={dropdownRef}>
      <Menu.Button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary transition-colors"
      >
        {unreadCount > 0 ? (
          <div className="relative">
            <BellSolidIcon className="w-6 h-6" />
            <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </div>
        ) : (
          <BellIcon className="w-6 h-6" />
        )}
      </Menu.Button>

      <Transition
        show={isOpen}
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items 
          static
          className="absolute right-0 z-50 mt-2 w-96 origin-top-right rounded-lg bg-white dark:bg-dark-card shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
        >
          <div className="py-1">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b dark:border-dark-border">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <BellIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-dark-text-tertiary" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-dark-text-secondary">
                    No new notifications
                  </p>
                </div>
              ) : (
                <>
                  {notifications.map((notification) => (
                    <Menu.Item key={notification.id}>
                      {({ active }) => (
                        <div
                          onClick={() => handleNotificationClick(notification)}
                          className={`
                            relative flex items-start px-4 py-3 cursor-pointer transition-colors
                            ${active ? 'bg-gray-50 dark:bg-dark-badge-gray' : ''}
                            ${!notification.isRead ? 'bg-blue-50 dark:bg-blue-900/10' : ''}
                          `}
                        >
                          {/* Icon */}
                          <div className="flex-shrink-0 mt-1">
                            {getNotificationIcon(notification.type)}
                          </div>

                          {/* Content */}
                          <div className="ml-3 flex-1">
                            <p className={`text-sm ${!notification.isRead ? 'font-semibold' : ''} text-gray-900 dark:text-dark-text-primary`}>
                              {notification.title}
                            </p>
                            <p className="mt-1 text-sm text-gray-600 dark:text-dark-text-secondary">
                              {notification.message}
                            </p>
                            
                            {/* Comment preview for COMMENT type notifications */}
                            {notification.type === 'COMMENT' && notification.data?.commentContent && (
                              <div className="mt-2">
                                <button
                                  onClick={(e) => toggleCommentPreview(e, notification.id)}
                                  className="flex items-center text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                                >
                                  {expandedComments.has(notification.id) ? (
                                    <>
                                      <ChevronUpIcon className="w-3 h-3 mr-1" />
                                      Hide comment
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDownIcon className="w-3 h-3 mr-1" />
                                      Preview comment
                                    </>
                                  )}
                                </button>
                                
                                {expandedComments.has(notification.id) && (
                                  <div className="mt-1 p-2 bg-gray-50 dark:bg-dark-badge-gray rounded text-xs text-gray-700 dark:text-dark-text-secondary whitespace-pre-wrap">
                                    {formatCommentPreview(notification.data.commentContent)}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <p className="mt-1 text-xs text-gray-500 dark:text-dark-text-tertiary">
                              {formatDistanceToNow(new Date(notification.createdAt), {
                                addSuffix: true,
                                locale: enUS,
                              })}
                            </p>
                          </div>

                          {/* Actions */}
                          <div className="flex-shrink-0 ml-2">
                            <button
                              onClick={(e) => handleDeleteNotification(e, notification.id)}
                              className="p-1 text-gray-400 hover:text-gray-500 dark:text-dark-text-tertiary dark:hover:text-dark-text-secondary"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Unread indicator */}
                          {!notification.isRead && (
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-500" />
                          )}
                        </div>
                      )}
                    </Menu.Item>
                  ))}

                  {/* Load more */}
                  {hasMore && (
                    <button
                      onClick={loadMore}
                      className="w-full px-4 py-2 text-sm text-center text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 hover:bg-gray-50 dark:hover:bg-dark-badge-gray"
                    >
                      Load more
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-4 py-3 border-t dark:border-dark-border">
                <button
                  onClick={() => {
                    navigate('/notifications');
                    setIsOpen(false);
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}